import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, FastForward } from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { displayReqNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusPill from "@/components/StatusPill";
import { formatDate } from "@/lib/format";

export default function Approvals() {
  const { persona } = usePersona();
  const [managers, setManagers] = useState([]);
  const [managerEmail, setManagerEmail] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decidingId, setDecidingId] = useState(null);
  const [notes, setNotes] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    endpoints.managers().then((m) => {
      setManagers(m);
      const match = m.find((x) => x.persona_id === persona.id);
      setManagerEmail((prev) => prev || (match?.email ?? m[0]?.email));
    });
  }, [persona.id]);

  const load = () => {
    if (!managerEmail) return;
    setLoading(true);
    endpoints.approvals(managerEmail, "Pending Approval")
      .then((d) => { setItems(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [managerEmail]);

  const decide = async (order, decision) => {
    setDecidingId(order.id);
    try {
      const actor = managers.find((m) => m.email === managerEmail);
      await endpoints.decideApproval(order.id, { decision, note: notes[order.id] || "", actor_name: actor?.name });
      toast.success(`Order ${displayReqNumber(order)} ${decision === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setDecidingId(null);
    }
  };

  const advanceTime = async () => {
    try {
      const res = await endpoints.advanceTime();
      if (res.fulfilled_count > 0) {
        toast.success(`Fast-forwarded 24h — ${res.fulfilled_count} order(s) fulfilled`);
      } else {
        toast.info("No approved orders waiting for auto-fulfillment.");
      }
    } catch { toast.error("Failed to advance time"); }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Approvals</div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">Manager Inbox</h1>
          <p className="text-sm text-slate-600 mt-1">Review and approve device requests over $1,000. Approved orders auto-fulfill in 24h.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={advanceTime} variant="outline" data-testid="advance-time-btn">
            <FastForward size={14} className="mr-2" /> Fast-forward 24h (demo)
          </Button>
        </div>
      </div>

      <Card className="p-4 border-slate-200 shadow-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <ShieldCheck size={18} className="text-[#0033A0]" />
          <div className="text-sm text-slate-700">You are reviewing as:</div>
          <Select value={managerEmail || ""} onValueChange={setManagerEmail}>
            <SelectTrigger data-testid="manager-select" className="w-72"><SelectValue placeholder="Choose a manager" /></SelectTrigger>
            <SelectContent>
              {managers.map((m) => (
                <SelectItem key={m.email} value={m.email} data-testid={`mgr-opt-${m.persona_id}`}>
                  {m.name} · {m.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-slate-500 ml-2">Manages persona: <span className="font-semibold text-slate-700">{managers.find((m) => m.email === managerEmail)?.persona_id || "—"}</span></div>
        </div>
      </Card>

      <div className="grid gap-4" data-testid="approvals-list">
        {loading && Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-40 w-full" />)}
        {!loading && items.length === 0 && (
          <Card className="p-10 text-center border-slate-200 shadow-sm">
            <CheckCircle2 className="mx-auto text-green-500 mb-3" size={32} />
            <div className="font-display font-bold text-lg text-slate-900">Inbox zero</div>
            <div className="text-sm text-slate-500">No orders pending your approval.</div>
          </Card>
        )}
        {items.map((o) => {
          const displayNum = displayReqNumber(o);
          return (
          <Card key={o.id} data-testid={`approval-card-${displayNum}`} className="p-5 border-slate-200 shadow-sm">
            <div className="grid md:grid-cols-[120px_1fr_auto] gap-5">
              <div className="rounded-md overflow-hidden bg-slate-100">
                <img src={o.device_image} alt="" className="w-full h-24 md:h-full object-cover" />
              </div>
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-sm font-bold text-[#0033A0]">{displayNum}</span>
                  {o.sn_number && o.sn_number !== o.req_number && (
                    <span className="font-mono text-[10px] text-slate-400" title="Local Flexspace REQ">fx {o.req_number}</span>
                  )}
                  <StatusPill status={o.status} />
                  <span className={`text-xs font-semibold ${o.priority === "Critical" ? "text-[#DA291C]" : o.priority === "High" ? "text-amber-600" : "text-slate-600"}`}>{o.priority}</span>
                </div>
                <div className="font-display font-bold text-lg text-slate-900 mt-1">{o.device_name}</div>
                <div className="text-sm text-slate-600 mt-0.5">
                  Requested by <span className="font-medium text-slate-900">{o.user_name}</span> · Delivery: {o.delivery_location} · {formatDate(o.created_at)}
                </div>
                <div className="text-sm text-slate-700 mt-2 border-l-2 border-slate-200 pl-3 italic">&ldquo;{o.justification}&rdquo;</div>
                <Textarea
                  data-testid={`approval-note-${displayNum}`}
                  placeholder="Optional note (visible on ticket)"
                  value={notes[o.id] || ""}
                  onChange={(e) => setNotes({ ...notes, [o.id]: e.target.value })}
                  className="mt-3 min-h-16"
                />
              </div>
              <div className="flex flex-col justify-between items-end gap-3 min-w-[180px]">
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Total</div>
                  <div className="font-display font-black text-2xl text-slate-900">${o.total_amount.toLocaleString()}</div>
                </div>
                <div className="flex gap-2 w-full">
                  <Button
                    variant="outline"
                    data-testid={`reject-${displayNum}`}
                    onClick={() => decide(o, "reject")}
                    disabled={decidingId === o.id}
                    className="flex-1 border-[#DA291C] text-[#DA291C] hover:bg-[#DA291C] hover:text-white"
                  >
                    Reject
                  </Button>
                  <Button
                    data-testid={`approve-${displayNum}`}
                    onClick={() => decide(o, "approve")}
                    disabled={decidingId === o.id}
                    className="flex-1 bg-[#0033A0] hover:bg-[#002573] text-white"
                  >
                    Approve
                  </Button>
                </div>
                <button
                  onClick={() => nav(`/tickets`)}
                  className="text-xs text-slate-500 hover:text-[#0033A0]"
                  data-testid={`view-ticket-${displayNum}`}
                >
                  View in ITSM →
                </button>
              </div>
            </div>
          </Card>
        );})}
      </div>
    </div>
  );
}
